export type TipoEntidadDescubrimiento = 'radio' | 'programa' | 'locutor' | 'club' | 'evento' | 'concurso';

export type BloqueDescubrimiento = 'porque-te-gusto' | 'tendencia' | 'nuevo-para-ti' | 'mas-popular';

export interface SenalInteraccion {
  seguimientos: number;
  likes: number;
  comentarios: number;
  compartidos: number;
  actividadReciente: number;
}

export interface RecomendacionDescubrimiento {
  id: number;
  tipo: TipoEntidadDescubrimiento;
  nombre: string;
  subtitulo: string;
  bloque: BloqueDescubrimiento;
  senales: SenalInteraccion;
  interactionScore: number;
  popularityScore: number;
  motivo: string;
  imagenUrl: string;
  logoUrl: string;
  link: string;
  badge: string;
  estaEnVivo: boolean;
  categoria: string;
  ciudad: string;
  pais: string;
  tags: string[];
  posicionBloque: number;
}

export interface BloqueInfo {
  id: BloqueDescubrimiento;
  titulo: string;
  subtitulo: string;
  icono: string;
  color: string;
  bg: string;
  borde: string;
  descripcion: string;
  gradiente: string;
}

export interface ReglaDescubrimiento {
  id: number;
  titulo: string;
  descripcion: string;
  icono: string;
  gradiente: string;
  colorBorde: string;
}

export interface StatsDescubrimiento {
  totalRecomendaciones: number;
  bloquesActivos: number;
  entidadesCubiertas: number;
  interaccionPromedio: number;
  precisionAlgoritmo: string;
  bloqueTop: string;
}

export const statsDescubrimiento: StatsDescubrimiento = {
  totalRecomendaciones: 24,
  bloquesActivos: 4,
  entidadesCubiertas: 6,
  interaccionPromedio: 87,
  precisionAlgoritmo: '94.3%',
  bloqueTop: 'Porque te gustó',
};

export const bloquesInfo: BloqueInfo[] = [
  {
    id: 'porque-te-gusto',
    titulo: 'Porque te gustó',
    subtitulo: 'Recomendaciones basadas en tus interacciones recientes: likes, comentarios y compartidos.',
    icono: 'ri-heart-line',
    color: 'text-rose-600',
    bg: 'bg-rose-50',
    borde: 'border-rose-200',
    descripcion: 'El algoritmo analiza tus últimas 50 interacciones y encuentra contenido similar que probablemente te va a encantar. Cuanto más interactuás, más preciso se vuelve.',
    gradiente: 'from-rose-500 to-pink-500',
  },
  {
    id: 'tendencia',
    titulo: 'Tendencia',
    subtitulo: 'Lo que está explotando ahora mismo. Basado en picos de actividad en tiempo real.',
    icono: 'ri-fire-line',
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    borde: 'border-amber-200',
    descripcion: 'Detectamos picos anormales de interacción en los últimos 15 minutos. Si algo está generando conversación masiva, lo ves acá primero.',
    gradiente: 'from-amber-500 to-orange-500',
  },
  {
    id: 'nuevo-para-ti',
    titulo: 'Nuevo para ti',
    subtitulo: 'Contenido fresco que aún no conocés. Descubrimiento puro, sin sesgo de popularidad.',
    icono: 'ri-sparkling-line',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    borde: 'border-emerald-200',
    descripcion: 'Radios, programas y locutores que se unieron recientemente o que nunca aparecieron en tu feed. La mejor forma de expandir tus horizontes radiales.',
    gradiente: 'from-emerald-500 to-teal-500',
  },
  {
    id: 'mas-popular',
    titulo: 'Más popular',
    subtitulo: 'Lo más escuchado, seguido y comentado de toda la plataforma. El pulso de la comunidad.',
    icono: 'ri-trophy-line',
    color: 'text-violet-600',
    bg: 'bg-violet-50',
    borde: 'border-violet-200',
    descripcion: 'Ranking por interacción real, no solo por oyentes. Una radio con 1000 oyentes pero 500 comentarios rankea más alto que una con 5000 oyentes y 10 comentarios.',
    gradiente: 'from-violet-500 to-purple-500',
  },
];

export const reglasDescubrimiento: ReglaDescubrimiento[] = [
  {
    id: 1,
    titulo: 'Priorizar Interacción Sobre Popularidad',
    descripcion: 'El algoritmo de descubrimiento pesa más las interacciones reales (comentarios, compartidos, likes) que las métricas pasivas (oyentes, seguidores). Una radio pequeña con una comunidad hiper-activa puede aparecer antes que una radio masiva con poca participación. Esto asegura que el contenido de calidad emerja naturalmente.',
    icono: 'ri-scales-3-line',
    gradiente: 'from-rose-500 to-amber-500',
    colorBorde: 'border-rose-200',
  },
  {
    id: 2,
    titulo: 'Cuatro Bloques, Cuatro Estrategias Diferentes',
    descripcion: 'Cada bloque usa una señal distinta: "Porque te gustó" usa tu historial de interacciones, "Tendencia" detecta picos en tiempo real, "Nuevo para ti" excluye lo que ya viste, y "Más popular" rankea por interacción comunitaria. Ningún bloque compite con otro — se complementan para cubrir todas las formas de descubrimiento.',
    icono: 'ri-layout-grid-line',
    gradiente: 'from-emerald-500 to-teal-500',
    colorBorde: 'border-emerald-200',
  },
  {
    id: 3,
    titulo: 'Seis Entidades, Una Sola Experiencia',
    descripcion: 'Radios, Programas, Locutores, Clubes, Eventos y Concursos comparten el mismo sistema de recomendación. No importa qué tipo de contenido estás buscando — el algoritmo entiende el contexto y te muestra lo más relevante en cada categoría.',
    icono: 'ri-stack-line',
    gradiente: 'from-violet-500 to-purple-500',
    colorBorde: 'border-violet-200',
  },
  {
    id: 4,
    titulo: 'El Algoritmo Aprende de Cada Interacción',
    descripcion: 'Cada like, cada comentario, cada compartido y cada seguimiento afina el modelo. Si empezás a interactuar más con contenido de rock, el sistema te mostrará más rock. Si dejás de interactuar con deportes, el sistema lo detecta y ajusta. Sin configuraciones manuales — el descubrimiento se vuelve más preciso con cada uso.',
    icono: 'ri-brain-line',
    gradiente: 'from-amber-500 to-orange-500',
    colorBorde: 'border-amber-200',
  },
];

export const tiposEntidad: { id: TipoEntidadDescubrimiento; label: string; icono: string; color: string; bg: string }[] = [
  { id: 'radio', label: 'Radio', icono: 'ri-radio-line', color: 'text-rose-600', bg: 'bg-rose-50' },
  { id: 'programa', label: 'Programa', icono: 'ri-mic-line', color: 'text-emerald-600', bg: 'bg-emerald-50' },
  { id: 'locutor', label: 'Locutor', icono: 'ri-user-voice-line', color: 'text-amber-600', bg: 'bg-amber-50' },
  { id: 'club', label: 'Club', icono: 'ri-group-line', color: 'text-sky-600', bg: 'bg-sky-50' },
  { id: 'evento', label: 'Evento', icono: 'ri-calendar-event-line', color: 'text-violet-600', bg: 'bg-violet-50' },
  { id: 'concurso', label: 'Concurso', icono: 'ri-trophy-line', color: 'text-orange-600', bg: 'bg-orange-50' },
];

export const recomendaciones: RecomendacionDescubrimiento[] = [
  // ═══════ BLOQUE: PORQUE TE GUSTÓ (6) — Basado en historial de interacciones ═══════
  {
    id: 1,
    tipo: 'radio',
    nombre: 'Rock Total Radio',
    subtitulo: '87.01 RFW · Buenos Aires',
    bloque: 'porque-te-gusto',
    senales: { seguimientos: 1, likes: 12, comentarios: 8, compartidos: 5, actividadReciente: 94 },
    interactionScore: 94,
    popularityScore: 78,
    motivo: 'Diste like a 12 posts de esta radio y comentaste en 8 de ellos.',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Dynamic%20rock%20radio%20station%20broadcast%20studio%20with%20electric%20guitars%20on%20wall%20and%20vintage%20vinyl%20collection%2C%20warm%20amber%20studio%20lighting%2C%20professional%20microphone%20setup%2C%20industrial%20loft%20aesthetic%2C%20editorial%20photography%20with%20soft%20shadows%20and%20warm%20color%20tones%2C%20passionate%20rock%20atmosphere&width=800&height=500&seq=desc-ptg-radio-01&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Rock%20radio%20station%20logo%20with%20electric%20guitar%20and%20lightning%20bolt%2C%20circular%20design%20black%20and%20red%20color%20scheme%2C%20modern%20rock%20broadcast%20emblem%2C%20clean%20vector%20style%20isolated%20on%20transparent%20background&width=200&height=200&seq=desc-ptg-logo-rd-01&orientation=squarish',
    link: '/radio/87.01 RFW',
    badge: 'Tu favorita',
    estaEnVivo: true,
    categoria: 'Rock',
    ciudad: 'Buenos Aires',
    pais: 'Argentina',
    tags: ['rock', 'guitarras', 'clásico', 'en vivo'],
    posicionBloque: 1,
  },
  {
    id: 2,
    tipo: 'locutor',
    nombre: 'Andrés Castillo',
    subtitulo: 'Rock Total Radio · Distorsión Total',
    bloque: 'porque-te-gusto',
    senales: { seguimientos: 1, likes: 9, comentarios: 15, compartidos: 7, actividadReciente: 96 },
    interactionScore: 96,
    popularityScore: 72,
    motivo: 'Seguís a este locutor y comentaste en 15 de sus transmisiones.',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Charismatic%20male%20radio%20host%20speaking%20passionately%20into%20professional%20microphone%20in%20modern%20studio%2C%20warm%20amber%20studio%20lighting%2C%20headphones%20around%20neck%2C%20expressive%20gesture%2C%20editorial%20portrait%20photography%20with%20shallow%20depth%20of%20field%2C%20clean%20background&width=800&height=500&seq=desc-ptg-locutor-01&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Male%20radio%20broadcaster%20avatar%20portrait%20with%20headphones%2C%20friendly%20expression%2C%20circular%20frame%2C%20warm%20lighting%2C%20professional%20headshot%20style%20isolated%20on%20light%20background&width=200&height=200&seq=desc-ptg-logo-lc-01&orientation=squarish',
    link: '/host/andres-castillo',
    badge: 'Tu locutor',
    estaEnVivo: true,
    categoria: 'Rock',
    ciudad: 'Buenos Aires',
    pais: 'Argentina',
    tags: ['rock', 'carismático', 'entrevistas', 'en vivo'],
    posicionBloque: 2,
  },
  {
    id: 3,
    tipo: 'programa',
    nombre: 'Distorsión Total',
    subtitulo: 'Rock Total Radio · Lun-Vie 16:00',
    bloque: 'porque-te-gusto',
    senales: { seguimientos: 0, likes: 7, comentarios: 11, compartidos: 4, actividadReciente: 88 },
    interactionScore: 88,
    popularityScore: 80,
    motivo: 'Comentaste 11 veces en este programa y lo compartiste 4 veces.',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Live%20rock%20radio%20program%20studio%20with%20host%20behind%20mixing%20console%2C%20electric%20guitar%20collection%20on%20wall%2C%20warm%20red%20and%20amber%20mood%20lighting%2C%20professional%20broadcast%20equipment%2C%20energetic%20atmosphere%2C%20editorial%20photography&width=800&height=500&seq=desc-ptg-prog-01&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Rock%20radio%20program%20show%20logo%20with%20distorted%20guitar%20waveform%20design%2C%20bold%20typography%2C%20red%20and%20black%20color%20scheme%2C%20circular%20emblem%2C%20clean%20vector%20style&width=200&height=200&seq=desc-ptg-logo-pg-01&orientation=squarish',
    link: '/program/distorsion-total',
    badge: 'No te lo pierdas',
    estaEnVivo: true,
    categoria: 'Rock',
    ciudad: 'Buenos Aires',
    pais: 'Argentina',
    tags: ['rock', 'tarde', 'música', 'entrevistas'],
    posicionBloque: 3,
  },
  {
    id: 4,
    tipo: 'club',
    nombre: 'Rockeros Unidos',
    subtitulo: 'Rock Total Radio · Club Premium',
    bloque: 'porque-te-gusto',
    senales: { seguimientos: 0, likes: 6, comentarios: 4, compartidos: 3, actividadReciente: 72 },
    interactionScore: 72,
    popularityScore: 65,
    motivo: 'Diste like a 6 publicaciones del club y compartiste 3 eventos.',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Rock%20music%20fan%20club%20gathering%20in%20industrial%20loft%20venue%2C%20people%20wearing%20band%20t-shirts%20socializing%2C%20warm%20amber%20lighting%20with%20string%20lights%2C%20vintage%20concert%20posters%20on%20wall%2C%20community%20atmosphere%2C%20documentary%20photography%20style&width=800&height=500&seq=desc-ptg-club-01&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Rock%20music%20fan%20club%20logo%20with%20guitar%20pick%20and%20crown%20design%2C%20circular%20emblem%2C%20black%20and%20gold%20color%20scheme%2C%20premium%20community%20badge%20style&width=200&height=200&seq=desc-ptg-logo-cl-01&orientation=squarish',
    link: '/clubes/rockeros-unidos',
    badge: 'Tu comunidad',
    estaEnVivo: false,
    categoria: 'Rock',
    ciudad: 'Buenos Aires',
    pais: 'Argentina',
    tags: ['rock', 'club', 'premium', 'eventos'],
    posicionBloque: 4,
  },
  {
    id: 5,
    tipo: 'radio',
    nombre: 'Electro Vibes FM',
    subtitulo: '96.50 RFW · Miami',
    bloque: 'porque-te-gusto',
    senales: { seguimientos: 0, likes: 8, comentarios: 5, compartidos: 6, actividadReciente: 81 },
    interactionScore: 81,
    popularityScore: 70,
    motivo: 'Compartiste 6 transmisiones de esta radio en la última semana.',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Futuristic%20electronic%20music%20radio%20studio%20with%20neon%20cyan%20and%20magenta%20lighting%2C%20DJ%20mixing%20console%2C%20synthesizer%20equipment%2C%20modern%20minimalist%20aesthetic%2C%20club%20atmosphere%2C%20editorial%20photography&width=800&height=500&seq=desc-ptg-radio-02&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Electronic%20music%20radio%20station%20logo%20with%20synthesizer%20waveform%20and%20neon%20accents%2C%20circular%20design%20vibrant%20cyan%20and%20magenta%20colors%2C%20futuristic%20style&width=200&height=200&seq=desc-ptg-logo-rd-02&orientation=squarish',
    link: '/radio/96.50 RFW',
    badge: 'Compartida por vos',
    estaEnVivo: true,
    categoria: 'Electrónica',
    ciudad: 'Miami',
    pais: 'Estados Unidos',
    tags: ['electrónica', 'miami', 'dj', 'nocturna'],
    posicionBloque: 5,
  },
  {
    id: 6,
    tipo: 'concurso',
    nombre: 'Concurso La Voz RFW 2026',
    subtitulo: 'RFW · Inscripciones Abiertas',
    bloque: 'porque-te-gusto',
    senales: { seguimientos: 0, likes: 4, comentarios: 6, compartidos: 2, actividadReciente: 68 },
    interactionScore: 68,
    popularityScore: 82,
    motivo: 'Participaste en concursos similares y comentaste en este.',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Exciting%20singing%20competition%20stage%20with%20spotlights%20and%20golden%20microphone%2C%20grand%20theater%20setting%2C%20dramatic%20lighting%2C%20concert%20atmosphere%2C%20professional%20event%20photography%20with%20theatrical%20elements&width=800&height=500&seq=desc-ptg-conc-01&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Singing%20competition%20logo%20with%20microphone%20and%20golden%20star%2C%20circular%20emblem%2C%20dramatic%20red%20and%20gold%20colors%2C%20talent%20show%20style&width=200&height=200&seq=desc-ptg-logo-cn-01&orientation=squarish',
    link: '/explorar',
    badge: 'Para vos',
    estaEnVivo: false,
    categoria: 'Musical',
    ciudad: 'Global',
    pais: 'RFW',
    tags: ['concurso', 'canto', 'premio', 'inscripción'],
    posicionBloque: 6,
  },

  // ═══════ BLOQUE: TENDENCIA (6) — Picos de actividad en tiempo real ═══════
  {
    id: 7,
    tipo: 'evento',
    nombre: 'Final del Campeonato Nacional',
    subtitulo: 'Deportes Total Radio · Hoy 15:00',
    bloque: 'tendencia',
    senales: { seguimientos: 0, likes: 28, comentarios: 45, compartidos: 32, actividadReciente: 98 },
    interactionScore: 98,
    popularityScore: 91,
    motivo: 'Pico de actividad: +320% de comentarios en los últimos 15 minutos.',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Soccer%20stadium%20packed%20with%20passionate%20fans%20waving%20flags%20under%20dramatic%20sunset%20sky%2C%20championship%20final%20match%20atmosphere%2C%20stadium%20floodlights%20glowing%2C%20confetti%20and%20banners%20in%20air%2C%20wide%20angle%20sports%20photography%20with%20vibrant%20colors&width=800&height=500&seq=desc-tnd-evento-01&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Sports%20radio%20station%20logo%20with%20soccer%20ball%20and%20trophy%20icon%2C%20circular%20design%20dark%20navy%20and%20gold%20color%20scheme%2C%20clean%20vector%20emblem%20style&width=200&height=200&seq=desc-tnd-logo-ev-01&orientation=squarish',
    link: '/eventos',
    badge: 'Explosivo ahora',
    estaEnVivo: true,
    categoria: 'Deportes',
    ciudad: 'Dallas',
    pais: 'Estados Unidos',
    tags: ['deportes', 'final', 'fútbol', 'campeonato'],
    posicionBloque: 1,
  },
  {
    id: 8,
    tipo: 'locutor',
    nombre: 'Isabella Torres',
    subtitulo: 'Radio Éxito FM · 98.7 FM',
    bloque: 'tendencia',
    senales: { seguimientos: 0, likes: 35, comentarios: 52, compartidos: 28, actividadReciente: 97 },
    interactionScore: 97,
    popularityScore: 85,
    motivo: 'Su entrevista de hoy generó 850 comentarios en 20 minutos.',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Professional%20female%20radio%20broadcaster%20portrait%20with%20headphones%20in%20modern%20studio%2C%20warm%20amber%20studio%20lighting%2C%20confident%20expression%2C%20editorial%20photography%20style%20with%20soft%20depth%20of%20field%2C%20clean%20neutral%20background&width=800&height=500&seq=desc-tnd-locutor-01&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Female%20radio%20broadcaster%20avatar%20portrait%20with%20headphones%2C%20warm%20smile%2C%20circular%20frame%2C%20soft%20lighting%2C%20professional%20headshot%20style&width=200&height=200&seq=desc-tnd-logo-lc-01&orientation=squarish',
    link: '/host/isabella-torres',
    badge: 'Viral hoy',
    estaEnVivo: true,
    categoria: 'Entretenimiento',
    ciudad: 'Medellín',
    pais: 'Colombia',
    tags: ['entretenimiento', 'entrevista', 'viral', 'carismática'],
    posicionBloque: 2,
  },
  {
    id: 9,
    tipo: 'programa',
    nombre: 'Carrusel Deportivo',
    subtitulo: 'Deportes Total Radio · Lun-Vie 18:00',
    bloque: 'tendencia',
    senales: { seguimientos: 0, likes: 22, comentarios: 38, compartidos: 25, actividadReciente: 93 },
    interactionScore: 93,
    popularityScore: 88,
    motivo: '+180% de shares en la última hora por cobertura de la final.',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Dynamic%20sports%20radio%20show%20studio%20with%20multiple%20microphones%20and%20large%20screens%20showing%20match%20highlights%2C%20energetic%20atmosphere%2C%20professional%20broadcast%20setup%2C%20warm%20studio%20lighting%2C%20editorial%20photography&width=800&height=500&seq=desc-tnd-prog-01&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Sports%20radio%20program%20logo%20with%20soccer%20ball%20and%20microphone%2C%20circular%20design%20navy%20and%20gold%2C%20energetic%20style&width=200&height=200&seq=desc-tnd-logo-pg-01&orientation=squarish',
    link: '/program/carrusel-deportivo',
    badge: 'Tendencia #1',
    estaEnVivo: true,
    categoria: 'Deportes',
    ciudad: 'Dallas',
    pais: 'Estados Unidos',
    tags: ['deportes', 'carrusel', 'análisis', 'en vivo'],
    posicionBloque: 3,
  },
  {
    id: 10,
    tipo: 'radio',
    nombre: 'La Kalle 96.3 FM',
    subtitulo: '96.30 RFW · Barranquilla',
    bloque: 'tendencia',
    senales: { seguimientos: 0, likes: 18, comentarios: 25, compartidos: 20, actividadReciente: 90 },
    interactionScore: 90,
    popularityScore: 79,
    motivo: '+250% de oyentes nuevos en las últimas 2 horas.',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Vibrant%20Caribbean%20urban%20radio%20studio%20with%20colorful%20street%20art%20murals%2C%20warm%20tropical%20lighting%2C%20DJ%20turntables%20and%20professional%20microphones%2C%20reggaeton%20and%20hip%20hop%20culture%20aesthetic%2C%20lively%20energetic%20broadcast%20atmosphere%2C%20editorial%20photography%20with%20saturated%20warm%20colors&width=800&height=500&seq=desc-tnd-radio-01&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Dynamic%20urban%20radio%20station%20logo%20street%20style%20typography%20graffiti%20inspired%20design%2C%20hip%20hop%20culture%20sound%20wave%2C%20modern%20edgy%20brand%20identity&width=200&height=200&seq=desc-tnd-logo-rd-01&orientation=squarish',
    link: '/radio/96.30 RFW',
    badge: 'Creciendo ya',
    estaEnVivo: true,
    categoria: 'Música Urbana',
    ciudad: 'Barranquilla',
    pais: 'Colombia',
    tags: ['urbana', 'reggaetón', 'caribe', 'auténtica'],
    posicionBloque: 4,
  },
  {
    id: 11,
    tipo: 'club',
    nombre: 'Soneros de Corazón',
    subtitulo: 'Radio Salsa Brava · Club Oficial',
    bloque: 'tendencia',
    senales: { seguimientos: 0, likes: 14, comentarios: 19, compartidos: 16, actividadReciente: 84 },
    interactionScore: 84,
    popularityScore: 68,
    motivo: 'El club duplicó su actividad de comentarios esta semana.',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Salsa%20dance%20club%20social%20event%20with%20couples%20dancing%20under%20warm%20colored%20lights%2C%20Latin%20Caribbean%20atmosphere%2C%20live%20band%20in%20background%2C%20festive%20community%20gathering%2C%20vibrant%20documentary%20photography&width=800&height=500&seq=desc-tnd-club-01&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Salsa%20music%20fan%20club%20logo%20with%20conga%20drums%20and%20tropical%20palm%20design%2C%20circular%20emblem%2C%20orange%20and%20yellow%20colors%2C%20Caribbean%20style&width=200&height=200&seq=desc-tnd-logo-cl-01&orientation=squarish',
    link: '/clubes/soneros-de-corazon',
    badge: 'Comunidad activa',
    estaEnVivo: false,
    categoria: 'Salsa',
    ciudad: 'San Juan',
    pais: 'Puerto Rico',
    tags: ['salsa', 'baile', 'caribe', 'club'],
    posicionBloque: 5,
  },
  {
    id: 12,
    tipo: 'concurso',
    nombre: 'Concurso Mejor Podcast RFW',
    subtitulo: 'RFW · Votación Abierta',
    bloque: 'tendencia',
    senales: { seguimientos: 0, likes: 16, comentarios: 22, compartidos: 18, actividadReciente: 86 },
    interactionScore: 86,
    popularityScore: 74,
    motivo: '+430% de participación en las últimas 24 horas.',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Podcast%20award%20ceremony%20setup%20with%20golden%20trophies%20and%20microphones%20on%20red%20carpet%2C%20glamorous%20event%20lighting%2C%20professional%20photography%2C%20celebration%20atmosphere%20with%20elegant%20decor&width=800&height=500&seq=desc-tnd-conc-01&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Podcast%20award%20competition%20logo%20with%20golden%20microphone%20and%20laurel%20wreath%2C%20circular%20emblem%2C%20elegant%20design&width=200&height=200&seq=desc-tnd-logo-cn-01&orientation=squarish',
    link: '/explorar',
    badge: 'Votá ahora',
    estaEnVivo: false,
    categoria: 'Cultural',
    ciudad: 'Global',
    pais: 'RFW',
    tags: ['podcast', 'concurso', 'votación', 'premio'],
    posicionBloque: 6,
  },

  // ═══════ BLOQUE: NUEVO PARA TI (6) — Contenido no visto ═══════
  {
    id: 13,
    tipo: 'radio',
    nombre: 'Indie Frequency FM',
    subtitulo: '105.80 RFW · Buenos Aires',
    bloque: 'nuevo-para-ti',
    senales: { seguimientos: 0, likes: 0, comentarios: 0, compartidos: 0, actividadReciente: 45 },
    interactionScore: 45,
    popularityScore: 55,
    motivo: 'Nunca interactuaste con esta radio. Similar a Rock Total Radio que te gusta.',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Indie%20rock%20radio%20station%20studio%20with%20vintage%20cassette%20tape%20collection%20and%20geometric%20wall%20art%2C%20warm%20amber%20and%20teal%20color%20scheme%2C%20alternative%20broadcast%20atmosphere%2C%20underground%20music%20radio%20feel%2C%20editorial%20photography%20with%20retro%20aesthetic&width=800&height=500&seq=desc-npt-radio-01&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Indie%20rock%20radio%20station%20logo%20with%20vintage%20cassette%20tape%20and%20geometric%20shapes%2C%20circular%20design%20warm%20amber%20and%20teal%20colors%2C%20alternative%20broadcast%20emblem&width=200&height=200&seq=desc-npt-logo-rd-01&orientation=squarish',
    link: '/radio/105.80 RFW',
    badge: 'Descubrí',
    estaEnVivo: true,
    categoria: 'Rock',
    ciudad: 'Buenos Aires',
    pais: 'Argentina',
    tags: ['indie', 'rock', 'alternativo', 'descubrimiento'],
    posicionBloque: 1,
  },
  {
    id: 14,
    tipo: 'locutor',
    nombre: 'Valentina Ríos',
    subtitulo: 'Radio Nueva Actitud · 94.70 RFW',
    bloque: 'nuevo-para-ti',
    senales: { seguimientos: 0, likes: 0, comentarios: 0, compartidos: 0, actividadReciente: 72 },
    interactionScore: 72,
    popularityScore: 68,
    motivo: 'Locutora revelación. Su estilo encaja con tus preferencias de contenido.',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Energetic%20female%20radio%20host%20laughing%20in%20bright%20modern%20studio%2C%20colorful%20headphones%2C%20natural%20daylight%20streaming%20through%20window%2C%20warm%20and%20inviting%20atmosphere%2C%20editorial%20portrait%20photography%20with%20soft%20natural%20light&width=800&height=500&seq=desc-npt-locutor-01&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Female%20radio%20broadcaster%20avatar%20portrait%20with%20headphones%2C%20warm%20smile%2C%20circular%20frame%2C%20soft%20lighting%2C%20professional%20headshot%20style&width=200&height=200&seq=desc-npt-logo-lc-01&orientation=squarish',
    link: '/host/valentina-rios',
    badge: 'Revelación',
    estaEnVivo: true,
    categoria: 'Pop',
    ciudad: 'Ciudad de México',
    pais: 'México',
    tags: ['pop', 'juvenil', 'revelación', 'carismática'],
    posicionBloque: 2,
  },
  {
    id: 15,
    tipo: 'programa',
    nombre: 'Mic Check Live',
    subtitulo: 'Radio Hip Hop Nation · Lun-Vie 20:00',
    bloque: 'nuevo-para-ti',
    senales: { seguimientos: 0, likes: 0, comentarios: 0, compartidos: 0, actividadReciente: 68 },
    interactionScore: 68,
    popularityScore: 73,
    motivo: 'Programa de hip hop con alta interacción. Nuevo en tu radar.',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Hip%20hop%20radio%20show%20with%20two%20hosts%20in%20urban%20style%20studio%2C%20graffiti%20art%20on%20walls%2C%20turntables%20and%20microphones%2C%20purple%20and%20gold%20mood%20lighting%2C%20street%20culture%20vibe%2C%20editorial%20photography&width=800&height=500&seq=desc-npt-prog-01&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Hip%20hop%20radio%20program%20logo%20with%20microphone%20and%20turntable%2C%20bold%20typography%2C%20gold%20and%20black%20colors%2C%20urban%20style&width=200&height=200&seq=desc-npt-logo-pg-01&orientation=squarish',
    link: '/program/mic-check-live',
    badge: 'Nuevo',
    estaEnVivo: true,
    categoria: 'Hip Hop',
    ciudad: 'Atlanta',
    pais: 'Estados Unidos',
    tags: ['hip hop', 'urbano', 'freestyle', 'batallas'],
    posicionBloque: 3,
  },
  {
    id: 16,
    tipo: 'club',
    nombre: 'Legión Deportiva',
    subtitulo: 'Deportes Total Radio · Club Oficial',
    bloque: 'nuevo-para-ti',
    senales: { seguimientos: 0, likes: 0, comentarios: 0, compartidos: 0, actividadReciente: 62 },
    interactionScore: 62,
    popularityScore: 60,
    motivo: 'Club con +6,300 miembros. Ideal si te gusta el contenido deportivo.',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Sports%20fan%20club%20watch%20party%20with%20fans%20in%20team%20jerseys%20cheering%20at%20large%20screen%2C%20excited%20atmosphere%2C%20flags%20and%20banners%2C%20community%20sports%20event%2C%20documentary%20photography&width=800&height=500&seq=desc-npt-club-01&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Sports%20fan%20club%20logo%20with%20shield%20and%20star%20design%2C%20circular%20emblem%2C%20navy%20and%20gold%20colors&width=200&height=200&seq=desc-npt-logo-cl-01&orientation=squarish',
    link: '/clubes/legion-deportiva',
    badge: 'Para fanáticos',
    estaEnVivo: false,
    categoria: 'Deportes',
    ciudad: 'Dallas',
    pais: 'Estados Unidos',
    tags: ['deportes', 'club', 'fanáticos', 'comunidad'],
    posicionBloque: 4,
  },
  {
    id: 17,
    tipo: 'evento',
    nombre: 'Summer Fest RFW 2026',
    subtitulo: 'RFW · 15 Julio 2026',
    bloque: 'nuevo-para-ti',
    senales: { seguimientos: 0, likes: 0, comentarios: 0, compartidos: 0, actividadReciente: 78 },
    interactionScore: 78,
    popularityScore: 82,
    motivo: 'El festival más grande del año. 18,500 personas ya confirmaron.',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Massive%20outdoor%20summer%20music%20festival%20with%20spectacular%20stage%20and%20LED%20screens%2C%20huge%20crowd%20dancing%20at%20golden%20hour%2C%20colorful%20confetti%20and%20smoke%20effects%2C%20epic%20wide%20angle%20festival%20photography&width=800&height=500&seq=desc-npt-evento-01&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Summer%20music%20festival%20logo%20with%20sun%20and%20palm%20tree%2C%20circular%20design%20with%20warm%20gradient%20colors%2C%20festive%20emblem&width=200&height=200&seq=desc-npt-logo-ev-01&orientation=squarish',
    link: '/eventos',
    badge: 'Imperdible',
    estaEnVivo: false,
    categoria: 'Festival',
    ciudad: 'Miami',
    pais: 'Estados Unidos',
    tags: ['festival', 'verano', 'música', 'masivo'],
    posicionBloque: 5,
  },
  {
    id: 18,
    tipo: 'concurso',
    nombre: 'Sorteo del Verano RFW',
    subtitulo: 'RFW · Hasta 30 Junio 2026',
    bloque: 'nuevo-para-ti',
    senales: { seguimientos: 0, likes: 0, comentarios: 0, compartidos: 0, actividadReciente: 55 },
    interactionScore: 55,
    popularityScore: 58,
    motivo: 'Concurso con premios exclusivos. Cierra en 9 días.',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Summer%20themed%20radio%20contest%20with%20beach%20prizes%20and%20colorful%20decorations%2C%20tropical%20vibes%2C%20bright%20sunny%20studio%20setup%2C%20festive%20atmosphere%20with%20palm%20leaves%20and%20golden%20accents&width=800&height=500&seq=desc-npt-conc-01&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Summer%20contest%20logo%20with%20beach%20ball%20and%20gift%20box%2C%20circular%20emblem%2C%20bright%20sunny%20colors%2C%20festive%20design&width=200&height=200&seq=desc-npt-logo-cn-01&orientation=squarish',
    link: '/explorar',
    badge: '9 días',
    estaEnVivo: false,
    categoria: 'Entretenimiento',
    ciudad: 'Global',
    pais: 'RFW',
    tags: ['sorteo', 'verano', 'premios', 'participar'],
    posicionBloque: 6,
  },

  // ═══════ BLOQUE: MÁS POPULAR (6) — Mayor interacción comunitaria ═══════
  {
    id: 19,
    tipo: 'radio',
    nombre: 'La Mexicana FM',
    subtitulo: '103.50 RFW · Houston',
    bloque: 'mas-popular',
    senales: { seguimientos: 0, likes: 42, comentarios: 38, compartidos: 30, actividadReciente: 92 },
    interactionScore: 92,
    popularityScore: 95,
    motivo: '#1 en interacción comunitaria. 56K seguidores, 12.4K oyentes.',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Mexican%20regional%20radio%20station%20studio%20with%20colorful%20decorations%20and%20traditional%20instruments%2C%20warm%20festive%20lighting%2C%20professional%20broadcast%20setup%2C%20cultural%20atmosphere%2C%20editorial%20photography%20with%20vibrant%20colors&width=800&height=500&seq=desc-mpop-radio-01&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Mexican%20regional%20radio%20station%20logo%20with%20guitar%20and%20sombrero%20elements%2C%20circular%20design%20warm%20orange%20and%20green%20colors%2C%20clean%20modern%20broadcast%20style&width=200&height=200&seq=desc-mpop-logo-rd-01&orientation=squarish',
    link: '/radio/103.50 RFW',
    badge: '#1 Comunidad',
    estaEnVivo: true,
    categoria: 'Regional Mexicana',
    ciudad: 'Houston',
    pais: 'Estados Unidos',
    tags: ['regional mexicana', 'houston', 'banda', 'tradición'],
    posicionBloque: 1,
  },
  {
    id: 20,
    tipo: 'programa',
    nombre: 'Despierta con Energía',
    subtitulo: 'Radio Éxito FM · Lun-Vie 06:00',
    bloque: 'mas-popular',
    senales: { seguimientos: 0, likes: 38, comentarios: 45, compartidos: 28, actividadReciente: 90 },
    interactionScore: 90,
    popularityScore: 89,
    motivo: 'El morning show con más comentarios diarios de toda la plataforma.',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Bright%20morning%20radio%20show%20studio%20with%20two%20hosts%20laughing%2C%20coffee%20cups%20on%20desk%2C%20sunny%20morning%20light%20streaming%20through%20window%2C%20energetic%20atmosphere%2C%20editorial%20photography&width=800&height=500&seq=desc-mpop-prog-01&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Morning%20radio%20show%20logo%20with%20sunrise%20and%20coffee%20cup%2C%20circular%20design%2C%20warm%20orange%20and%20yellow%20colors%2C%20energetic%20style&width=200&height=200&seq=desc-mpop-logo-pg-01&orientation=squarish',
    link: '/program/despierta-con-energia',
    badge: 'Morning #1',
    estaEnVivo: true,
    categoria: 'Entretenimiento',
    ciudad: 'Medellín',
    pais: 'Colombia',
    tags: ['morning', 'entretenimiento', 'humor', 'noticias'],
    posicionBloque: 2,
  },
  {
    id: 21,
    tipo: 'locutor',
    nombre: 'Javier Solís',
    subtitulo: 'Deportes Total Radio · 120.00 RFW',
    bloque: 'mas-popular',
    senales: { seguimientos: 0, likes: 30, comentarios: 35, compartidos: 22, actividadReciente: 85 },
    interactionScore: 85,
    popularityScore: 81,
    motivo: 'El locutor deportivo con mayor engagement por emisión.',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Passionate%20male%20sports%20broadcaster%20gesturing%20while%20speaking%2C%20headphones%20around%20neck%2C%20dynamic%20studio%20with%20sports%20memorabilia%2C%20professional%20lighting%2C%20editorial%20portrait&width=800&height=500&seq=desc-mpop-locutor-01&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Sports%20male%20broadcaster%20avatar%20with%20headset%2C%20professional%20look%2C%20circular%20frame&width=200&height=200&seq=desc-mpop-logo-lc-01&orientation=squarish',
    link: '/host/javier-solis',
    badge: 'Voz deportiva',
    estaEnVivo: true,
    categoria: 'Deportes',
    ciudad: 'Dallas',
    pais: 'Estados Unidos',
    tags: ['deportes', 'análisis', 'fútbol', 'carismático'],
    posicionBloque: 3,
  },
  {
    id: 22,
    tipo: 'club',
    nombre: 'Familia Mexicana FM',
    subtitulo: 'La Mexicana FM · Club Oficial',
    bloque: 'mas-popular',
    senales: { seguimientos: 0, likes: 26, comentarios: 30, compartidos: 20, actividadReciente: 80 },
    interactionScore: 80,
    popularityScore: 76,
    motivo: '7,200 miembros. El club más grande de la categoría Regional Mexicana.',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Mexican%20family%20gathering%20community%20event%20with%20food%20and%20music%2C%20outdoor%20fiesta%20atmosphere%2C%20colorful%20decorations%2C%20warm%20evening%20lighting%2C%20cultural%20documentary%20photography&width=800&height=500&seq=desc-mpop-club-01&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Mexican%20family%20community%20club%20logo%20with%20heart%20and%20sombrero%2C%20circular%20emblem%2C%20warm%20orange%20and%20green%20colors&width=200&height=200&seq=desc-mpop-logo-cl-01&orientation=squarish',
    link: '/clubes/familia-mexicana-fm',
    badge: '7.2K miembros',
    estaEnVivo: false,
    categoria: 'Regional Mexicana',
    ciudad: 'Houston',
    pais: 'Estados Unidos',
    tags: ['familia', 'mexicana', 'club', 'tradición'],
    posicionBloque: 4,
  },
  {
    id: 23,
    tipo: 'evento',
    nombre: 'Festival Sonoro RFW',
    subtitulo: 'Bogotá · 15 Julio 2026',
    bloque: 'mas-popular',
    senales: { seguimientos: 0, likes: 34, comentarios: 28, compartidos: 36, actividadReciente: 88 },
    interactionScore: 88,
    popularityScore: 87,
    motivo: 'El evento más compartido del mes. 8,200 asistentes confirmados.',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Massive%20outdoor%20music%20festival%20crowd%20with%20colorful%20stage%20lights%20at%20sunset%2C%20thousands%20of%20people%2C%20festival%20vibes%2C%20wide%20angle%20aerial%20perspective%2C%20epic%20concert%20photography%2C%20vibrant%20atmosphere%20with%20confetti%20and%20lasers&width=800&height=500&seq=desc-mpop-evento-01&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Music%20festival%20logo%20with%20sound%20waves%20and%20stars%2C%20circular%20design%2C%20vibrant%20multi-color%20gradient%2C%20festive%20emblem&width=200&height=200&seq=desc-mpop-logo-ev-01&orientation=squarish',
    link: '/eventos',
    badge: 'Más compartido',
    estaEnVivo: false,
    categoria: 'Musical',
    ciudad: 'Bogotá',
    pais: 'Colombia',
    tags: ['festival', 'música', 'bogotá', 'multitudinario'],
    posicionBloque: 5,
  },
  {
    id: 24,
    tipo: 'concurso',
    nombre: 'Concurso Creatividad Sonora',
    subtitulo: 'RFW · Recepción de Propuestas',
    bloque: 'mas-popular',
    senales: { seguimientos: 0, likes: 20, comentarios: 24, compartidos: 15, actividadReciente: 74 },
    interactionScore: 74,
    popularityScore: 70,
    motivo: '3,800 propuestas recibidas. La comunidad creativa está inspirada.',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Creative%20audio%20production%20contest%20setup%20with%20mixing%20console%20and%20award%20certificates%2C%20colorful%20sound%20wave%20visualizations%2C%20modern%20studio%2C%20innovative%20atmosphere%20with%20vibrant%20lighting&width=800&height=500&seq=desc-mpop-conc-01&orientation=landscape',
    logoUrl: 'https://readdy.ai/api/search-image?query=Creative%20audio%20contest%20logo%20with%20sound%20wave%20and%20lightbulb%2C%20circular%20emblem%2C%20colorful%20gradient%20design%2C%20innovative%20style&width=200&height=200&seq=desc-mpop-logo-cn-01&orientation=squarish',
    link: '/explorar',
    badge: 'Creativo',
    estaEnVivo: false,
    categoria: 'Cultural',
    ciudad: 'Global',
    pais: 'RFW',
    tags: ['creatividad', 'audio', 'concurso', 'propuestas'],
    posicionBloque: 6,
  },
];

export function getRecomendacionesPorBloque(bloque: BloqueDescubrimiento): RecomendacionDescubrimiento[] {
  return recomendaciones
    .filter(r => r.bloque === bloque)
    .sort((a, b) => a.posicionBloque - b.posicionBloque);
}

export function getRecomendacionesPorTipo(tipo: TipoEntidadDescubrimiento): RecomendacionDescubrimiento[] {
  return recomendaciones
    .filter(r => r.tipo === tipo)
    .sort((a, b) => b.interactionScore - a.interactionScore);
}

export function getTopPorInteraccion(n: number = 10): RecomendacionDescubrimiento[] {
  return [...recomendaciones]
    .sort((a, b) => b.interactionScore - a.interactionScore)
    .slice(0, n);
}

export function getRecomendacionesEnVivo(): RecomendacionDescubrimiento[] {
  return recomendaciones.filter(r => r.estaEnVivo);
}

export function getTodasPorBloque(): { bloque: BloqueInfo; items: RecomendacionDescubrimiento[] }[] {
  return bloquesInfo.map(bloque => ({
    bloque,
    items: getRecomendacionesPorBloque(bloque.id),
  }));
}

export function formatearNumero(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}